import React, { useState, useEffect, useRef, useCallback } from 'react';
import { AppBar, Toolbar, Box, MenuItem, Button, Typography, Avatar, Modal, IconButton, Tooltip, TextField, InputAdornment, Dialog, DialogTitle, DialogContent, DialogActions, Grid, Badge, Drawer, List, ListItemButton, ListItemIcon, ListItemText, Divider, BottomNavigation, BottomNavigationAction, useTheme, useMediaQuery, Card, CardContent } from '@mui/material';
import LoginIcon from '@mui/icons-material/Login';
import SearchIcon from '@mui/icons-material/Search';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ClearIcon from '@mui/icons-material/Clear';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AssignmentIcon from '@mui/icons-material/Assignment';
import HomeIcon from '@mui/icons-material/Home';
import SendIcon from '@mui/icons-material/Send';
import BuildIcon from '@mui/icons-material/Build';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import FavoriteIcon from '@mui/icons-material/Favorite';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import StorefrontIcon from '@mui/icons-material/Storefront';
import SellOutlinedIcon from '@mui/icons-material/SellOutlined';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import LoginPage from './LoginPage'; // Форма входа
import RegisterPage from './RegisterPage'; // Форма регистрации
import SearchField from './SearchField';
import { openPartnerApp } from '../utils/ssoAuth';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';

const BEAR_PAW_AVATAR = `${process.env.PUBLIC_URL || ''}/bear-paw.svg`;

const drawerMenuListSx = {
  '& .MuiListItemButton-root': { cursor: 'pointer' },
  '& .MuiListItemText-root, & .MuiListItemText-root .MuiTypography-root': {
    cursor: 'pointer',
    userSelect: 'none',
  },
};

const AppBarComponent = ({ user, logout, onProfileUpdate }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [isRegister, setIsRegister] = useState(false); // Состояние для переключения между входом и регистрацией
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState('');
  const [branchPickerOpen, setBranchPickerOpen] = useState(false);
  const [cartItemsCount, setCartItemsCount] = useState(0);
  const [mobileNavCompact, setMobileNavCompact] = useState(false); // на мобильной: при скролле вниз — только строка поиска
  const [telegramLinkLoading, setTelegramLinkLoading] = useState(false);
  /** Внешний placeholder и битая ссылка на фото давали «пустой» аватар */
  const [userAvatarImgError, setUserAvatarImgError] = useState(false);
  const lastScrollYRef = useRef(0);
  const accumulatedDeltaRef = useRef(0);   // накопленная дельта скролла в текущем направлении (вверх < 0, вниз > 0)
  const mobileNavCompactRef = useRef(false);
  const scrollRafRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const searchDebounceRef = useRef(null);

  const navigateToSearch = useCallback(
    (rawQuery, { replace = false } = {}) => {
      const q = String(rawQuery || '').trim();
      const params = new URLSearchParams();
      if (q) {
        params.set('q', q);
        localStorage.setItem('search', q);
      } else {
        localStorage.removeItem('search');
      }
      if (selectedBranch) params.set('branchId', selectedBranch);
      if (location.pathname === '/search') {
        const current = new URLSearchParams(location.search);
        if (current.get('type')) params.set('type', current.get('type'));
      }
      const target = q ? `/search?${params.toString()}` : '/search';
      navigate(target, { replace });
    },
    [navigate, selectedBranch, location.pathname, location.search]
  );

  const handleConnectTelegram = async () => {
    setTelegramLinkLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const res = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/profile/telegram-link`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.url) {
        window.open(data.url, '_blank');
      }
    } catch (e) {
      console.error('Ошибка получения ссылки Telegram:', e);
    } finally {
      setTelegramLinkLoading(false);
      handleMobileMenuClose();
    }
  };

  const handleMobileMenuOpen = () => setMobileMenuOpen(true);
  const handleMobileMenuClose = () => setMobileMenuOpen(false);

  useEffect(() => {
    setUserAvatarImgError(false);
  }, [user?.avatar, user?._id]);

  const renderUserAvatar = useCallback(
    (size = 'sm') => {
      if (!user) return null;
      const dim = size === 'md' ? 36 : 32;
      const userPhotoSrc =
        user.avatar && !userAvatarImgError
          ? `${process.env.REACT_APP_SERVER_URL}/${String(user.avatar).replace(/^\//, '')}`
          : null;
      const displaySrc = userPhotoSrc || BEAR_PAW_AVATAR;
      return (
        <Avatar
          src={displaySrc}
          alt={userPhotoSrc ? user.username || 'Пользователь' : 'Медвежий след Берлоги'}
          imgProps={{
            onError: () => {
              if (user?.avatar && !userAvatarImgError) setUserAvatarImgError(true);
            },
          }}
          sx={{
            width: dim,
            height: dim,
            flexShrink: 0,
            bgcolor: 'rgba(255,255,255,0.92)',
            color: '#3d362f',
            border: '1px solid rgba(0,0,0,0.12)',
            '& .MuiAvatar-img': {
              objectFit: userPhotoSrc ? 'cover' : 'contain',
              transform: userPhotoSrc ? 'none' : 'scale(0.88)',
            },
          }}
        />
      );
    },
    [user, userAvatarImgError]
  );

  const handleLogout = () => {
    if (window.confirm('Вы уверены, что хотите выйти?')) {
      logout();
      setMobileMenuOpen(false);
      window.location.reload();
    }
  };

  const handleOpenProApp = () => {
    handleMobileMenuClose();
    openPartnerApp('/landlord');
  };

  const updateURLParams = (updates) => {
    const searchParams = new URLSearchParams(location.search);
    
    // Обновляем параметры
    Object.keys(updates).forEach(key => {
      const value = updates[key];
      // Для дат не используем trim(), так как они уже в правильном формате
      if (key === 'dateFrom' || key === 'dateTo') {
        if (value && value.length > 0) {
          searchParams.set(key, value);
          // Сохраняем в localStorage
          localStorage.setItem(key, value);
        } else {
          searchParams.delete(key);
          // Удаляем из localStorage
          localStorage.removeItem(key);
        }
      } else if (key === 'branchId') {
        if (value && String(value).trim()) {
          searchParams.set(key, String(value).trim());
          localStorage.setItem('selectedBranch', value);
        } else {
          searchParams.delete(key);
          localStorage.removeItem('selectedBranch');
        }
      } else {
        // Для других параметров (например, search) используем trim()
        if (value && String(value).trim()) {
          searchParams.set(key, String(value).trim());
          localStorage.setItem(key, String(value).trim());
        } else {
          searchParams.delete(key);
          localStorage.removeItem(key);
        }
      }
    });
    
    // Если мы на главной или странице аренды, обновляем URL без перезагрузки
    if (location.pathname === '/' || location.pathname === '/rental') {
      navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true });
    } else if (location.pathname !== '/search') {
      // С других страниц — на каталог аренды с фильтрами (без поиска)
      navigate(`/rental?${searchParams.toString()}`);
    }
  };

  const handleSearchChange = (value) => {
    setSearchQuery(value);
    if (location.pathname === '/search') {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = setTimeout(() => {
        navigateToSearch(value, { replace: true });
      }, 400);
    }
  };

  const handleSearchSubmit = (query) => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    const q = String(query ?? searchQuery).trim();
    setSearchQuery(q);
    navigateToSearch(q);
  };

  const handleDateFromChange = (e) => {
    const value = e.target.value;
    setDateFrom(value);
    // Не обновляем URL при изменении дат внутри диалога
    // URL обновится только при нажатии кнопки "Готово"
  };

  const handleDateToChange = (e) => {
    const value = e.target.value;
    setDateTo(value);
    // Не обновляем URL при изменении дат внутри диалога
    // URL обновится только при нажатии кнопки "Готово"
  };

  const handleDateRangeClick = () => {
    setDatePickerOpen(true);
  };

  const handleDateRangeClose = () => {
    setDatePickerOpen(false);
  };

  const handleDateRangeConfirm = () => {
    // Применяем фильтр только если обе даты выбраны
    if (dateFrom && dateTo) {
      updateURLParams({ dateFrom: dateFrom, dateTo: dateTo });
      setDatePickerOpen(false);
    } else {
      // Можно показать предупреждение, что нужно выбрать обе даты
      alert('Пожалуйста, выберите обе даты (начала и окончания периода)');
    }
  };

  const handleClearDates = () => {
    setDateFrom('');
    setDateTo('');
    updateURLParams({ dateFrom: '', dateTo: '' });
  };

  const handleBranchChange = async (e) => {
    const value = e.target.value;
    setSelectedBranch(value);
    updateURLParams({ branchId: value });
    if (user && onProfileUpdate) {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const res = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/profile`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ preferredBranchId: value || null }),
        });
        if (res.ok) {
          const updatedUser = await res.json();
          onProfileUpdate(updatedUser);
        }
      } catch (err) {
        console.error('Ошибка сохранения центра выдачи:', err);
      }
    }
  };

  // Загружаем список филиалов
  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/admin/branches/public`);
        if (response.ok) {
          const data = await response.json();
          setBranches(data);
        }
      } catch (error) {
        console.error('Ошибка загрузки филиалов:', error);
      }
    };
    fetchBranches();
  }, []);

  // Загружаем количество товаров в корзине
  useEffect(() => {
    const fetchCartItemsCount = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        setCartItemsCount(0);
        return;
      }

      try {
        const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/cart`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (response.ok) {
          const data = await response.json();
          const totalCount = data.items?.reduce((sum, item) => sum + (item.quantity || 1), 0) || 0;
          setCartItemsCount(totalCount);
        }
      } catch (error) {
        console.error('Ошибка загрузки корзины:', error);
      }
    };

    fetchCartItemsCount();
    // Обновляем счетчик при изменении пути (когда добавляем товар в корзину или переходим на другие страницы)
    // Также обновляем при изменении токена (логин/логаут)
    const interval = setInterval(fetchCartItemsCount, 3000);
    return () => clearInterval(interval);
  }, [location.pathname, user]);

  // Восстанавливаем параметры при переходе на главную или аренду
  useEffect(() => {
    if (location.pathname === '/' || location.pathname === '/rental') {
      const searchParams = new URLSearchParams(location.search);
      
      const urlSearch = searchParams.get('search');
      const urlDateFrom = searchParams.get('dateFrom');
      const urlDateTo = searchParams.get('dateTo');
      const urlBranch = searchParams.get('branchId');
      
      const savedSearch = localStorage.getItem('search') || '';
      const savedDateFrom = localStorage.getItem('dateFrom') || '';
      const savedDateTo = localStorage.getItem('dateTo') || '';
      const savedBranch = localStorage.getItem('selectedBranch') || '';
      const branchFromProfile = user?.preferredBranchId || null;
      const branchToRestore = urlBranch || branchFromProfile || savedBranch;

      if (urlSearch && urlSearch.trim()) {
        const params = new URLSearchParams(location.search);
        params.delete('search');
        params.set('q', urlSearch.trim());
        navigate(`/search?${params.toString()}`, { replace: true });
        return;
      }
      
      const restoreParams = new URLSearchParams(location.search);
      let needsRestore = false;
      
      if (!urlDateFrom && savedDateFrom) {
        restoreParams.set('dateFrom', savedDateFrom);
        needsRestore = true;
      }
      if (!urlDateTo && savedDateTo) {
        restoreParams.set('dateTo', savedDateTo);
        needsRestore = true;
      }
      if (!urlBranch && branchToRestore) {
        restoreParams.set('branchId', branchToRestore);
        needsRestore = true;
      }
      
      if (needsRestore) {
        navigate(`${location.pathname}?${restoreParams.toString()}`, { replace: true });
      }
    }
  }, [location.pathname, user?.preferredBranchId, location.search, navigate]);

  // Инициализируем параметры из URL
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const search =
      location.pathname === '/search'
        ? searchParams.get('q') || searchParams.get('search') || ''
        : '';
    const from = searchParams.get('dateFrom') || '';
    const to = searchParams.get('dateTo') || '';
    const branch = searchParams.get('branchId') || '';
    setSearchQuery(search);
    setDateFrom(from);
    setDateTo(to);
    setSelectedBranch(branch);
  }, [location.search, location.pathname]);

  // Синхронизируем ref с состоянием навбара (для гистерезиса в scroll)
  useEffect(() => {
    mobileNavCompactRef.current = mobileNavCompact;
  }, [mobileNavCompact]);

  // Мобильная версия: при скролле вниз — компактный навбар, при скролле вверх на те же ~70px — полный, в любой точке страницы.
  // Накопление дельты по направлению: скролл вниз на 70px → compact, скролл вверх на 70px → full.
  useEffect(() => {
    if (!isMobile) return;
    const SCROLL_DELTA = 70;

    const handleScroll = () => {
      if (scrollRafRef.current) return;
      scrollRafRef.current = requestAnimationFrame(() => {
        scrollRafRef.current = null;
        const scrollY = window.scrollY || window.pageYOffset;
        const prevY = lastScrollYRef.current;
        const delta = scrollY - prevY;
        lastScrollYRef.current = scrollY;

        const isCompact = mobileNavCompactRef.current;
        let acc = accumulatedDeltaRef.current;

        // Смена направления — сбрасываем накопление и начинаем считать в новую сторону
        if (delta > 0 && acc < 0) acc = 0;
        if (delta < 0 && acc > 0) acc = 0;
        acc += delta;
        accumulatedDeltaRef.current = acc;

        if (isCompact) {
          if (acc <= -SCROLL_DELTA) {
            accumulatedDeltaRef.current = 0;
            setMobileNavCompact(false);
          }
        } else {
          if (acc >= SCROLL_DELTA) {
            accumulatedDeltaRef.current = 0;
            setMobileNavCompact(true);
          }
        }
      });
    };

    lastScrollYRef.current = window.scrollY || 0;
    accumulatedDeltaRef.current = 0;

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollRafRef.current) cancelAnimationFrame(scrollRafRef.current);
    };
  }, [isMobile]);

  const renderMobileMenu = () => (
    <Drawer
      anchor="right"
      open={mobileMenuOpen}
      onClose={handleMobileMenuClose}
    >
      <Box sx={{ width: 250, p: 2 }}>
        {user && (
          <>
            <ListItemButton
              onClick={() => {
                navigate('/profile');
                handleMobileMenuClose();
              }}
              sx={{
                mb: 2,
                pb: 2,
                borderBottom: '1px solid #e0e0e0',
                borderRadius: 0,
                px: 0,
                '&:hover': { backgroundColor: 'rgba(139, 125, 107, 0.08)' },
              }}
            >
              <Box sx={{ mr: 2 }}>{renderUserAvatar('md')}</Box>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                {user.username || 'Пользователь'}
              </Typography>
            </ListItemButton>
          </>
        )}
        <List sx={drawerMenuListSx}>
          {user ? (
            <>
              <ListItemButton onClick={() => { navigate('/orders'); handleMobileMenuClose(); }}>
                <ListItemIcon><ReceiptIcon /></ListItemIcon>
                <ListItemText primary="Мои заказы" />
              </ListItemButton>
              <ListItemButton onClick={() => { navigate('/favorites'); handleMobileMenuClose(); }}>
                <ListItemIcon><FavoriteIcon /></ListItemIcon>
                <ListItemText primary="Избранное" />
              </ListItemButton>
              <ListItemButton onClick={() => { navigate('/cart'); handleMobileMenuClose(); }}>
                <ListItemIcon><ShoppingCartIcon /></ListItemIcon>
                <ListItemText primary="Корзина" />
                {cartItemsCount > 0 && (
                  <Badge badgeContent={cartItemsCount} color="error" />
                )}
              </ListItemButton>
              <ListItemButton onClick={() => { navigate('/bonus'); handleMobileMenuClose(); }}>
                <ListItemIcon><AccountBalanceWalletIcon /></ListItemIcon>
                <ListItemText primary="Бонусы" />
              </ListItemButton>
              <ListItemButton onClick={() => { navigate('/rental'); handleMobileMenuClose(); }}>
                <ListItemIcon><Inventory2Icon /></ListItemIcon>
                <ListItemText primary="Аренда" />
              </ListItemButton>
              <ListItemButton onClick={() => { navigate('/services'); handleMobileMenuClose(); }}>
                <ListItemIcon><BuildIcon /></ListItemIcon>
                <ListItemText primary="Услуги" />
              </ListItemButton>
              <ListItemButton onClick={() => { navigate('/products'); handleMobileMenuClose(); }}>
                <ListItemIcon><StorefrontIcon /></ListItemIcon>
                <ListItemText primary="Товары" />
              </ListItemButton>
              <ListItemButton onClick={() => { navigate('/tours'); handleMobileMenuClose(); }}>
                <ListItemIcon><FlightTakeoffIcon /></ListItemIcon>
                <ListItemText primary="Туры" />
              </ListItemButton>
              <ListItemButton onClick={() => { navigate({ pathname: '/partners', hash: '#service' }); handleMobileMenuClose(); }}>
                <ListItemIcon><AddCircleOutlineIcon /></ListItemIcon>
                <ListItemText primary="Разместить услугу" />
              </ListItemButton>
              <ListItemButton onClick={() => { navigate({ pathname: '/partners', hash: '#equipment' }); handleMobileMenuClose(); }}>
                <ListItemIcon><AddCircleOutlineIcon /></ListItemIcon>
                <ListItemText primary="Сдать своё оборудование" />
              </ListItemButton>
              <ListItemButton onClick={() => { navigate('/terms'); handleMobileMenuClose(); }}>
                <ListItemIcon><AssignmentIcon /></ListItemIcon>
                <ListItemText primary="Оферта" />
              </ListItemButton>
              <ListItemButton onClick={handleConnectTelegram} disabled={telegramLinkLoading}>
                <ListItemIcon><SendIcon /></ListItemIcon>
                <ListItemText primary={telegramLinkLoading ? 'Загрузка...' : 'Подключить Telegram'} />
              </ListItemButton>
              <Divider sx={{ my: 1 }} />
              <ListItemButton onClick={handleOpenProApp}>
                <ListItemIcon><BusinessCenterIcon /></ListItemIcon>
                <ListItemText primary="BERLOGA PRO" />
              </ListItemButton>
              <ListItemButton onClick={handleLogout}>
                <ListItemIcon><LoginIcon /></ListItemIcon>
                <ListItemText primary="Выйти" />
              </ListItemButton>
            </>
          ) : (
            <>
              <ListItemButton onClick={() => { navigate('/rental'); handleMobileMenuClose(); }}>
                <ListItemIcon><Inventory2Icon /></ListItemIcon>
                <ListItemText primary="Аренда" />
              </ListItemButton>
              <ListItemButton onClick={() => { navigate('/services'); handleMobileMenuClose(); }}>
                <ListItemIcon><BuildIcon /></ListItemIcon>
                <ListItemText primary="Услуги" />
              </ListItemButton>
              <ListItemButton onClick={() => { navigate('/products'); handleMobileMenuClose(); }}>
                <ListItemIcon><StorefrontIcon /></ListItemIcon>
                <ListItemText primary="Товары" />
              </ListItemButton>
              <ListItemButton onClick={() => { navigate('/tours'); handleMobileMenuClose(); }}>
                <ListItemIcon><FlightTakeoffIcon /></ListItemIcon>
                <ListItemText primary="Туры" />
              </ListItemButton>
              <ListItemButton onClick={() => { navigate({ pathname: '/partners', hash: '#service' }); handleMobileMenuClose(); }}>
                <ListItemIcon><AddCircleOutlineIcon /></ListItemIcon>
                <ListItemText primary="Разместить услугу" />
              </ListItemButton>
              <ListItemButton onClick={() => { navigate({ pathname: '/partners', hash: '#equipment' }); handleMobileMenuClose(); }}>
                <ListItemIcon><AddCircleOutlineIcon /></ListItemIcon>
                <ListItemText primary="Сдать своё оборудование" />
              </ListItemButton>
              <ListItemButton onClick={() => {
                setIsRegister(false);
                setLoginModalOpen(true);
                handleMobileMenuClose();
              }}>
                <ListItemIcon><LoginIcon /></ListItemIcon>
                <ListItemText primary="Вход/Регистрация" />
              </ListItemButton>
            </>
          )}
        </List>
      </Box>
    </Drawer>
  );

  return (
    <>
      <AppBar
        position="sticky"
        sx={{
          backgroundColor: '#8B7D6B', // Коричнево-серый цвет из логотипа
          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.2)',
          top: 0,
          zIndex: 1101,
          borderBottomLeftRadius: { xs: '10px', md: 0 },
          borderBottomRightRadius: { xs: '10px', md: 0 },
        }}
      >
        <Toolbar sx={{ flexDirection: { xs: 'column', md: 'row' }, py: { xs: 0.25, md: 0 }, px: { xs: 1, md: 2 }, minHeight: { xs: 'auto', md: '64px' } }}>
          {/* Мобильная версия: при скролле вниз — только строка поиска + иконка; при скролле вверх — полный навбар */}
          {isMobile && mobileNavCompact && (
            <Box
              sx={{
                display: 'flex',
                width: '100%',
                alignItems: 'center',
                gap: 1,
                py: 0.5,
              }}
            >
              <SearchField
                value={searchQuery}
                onChange={handleSearchChange}
                onSubmit={handleSearchSubmit}
                onNavigate={(path) => navigate(path)}
                branchId={selectedBranch}
                sx={{ flex: 1, minWidth: 0 }}
              />
              {user ? (
                <IconButton
                  onClick={handleMobileMenuOpen}
                  sx={{ color: '#000000', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.1)' } }}
                >
                  {renderUserAvatar('sm')}
                </IconButton>
              ) : (
                <IconButton
                  onClick={() => { setIsRegister(false); setLoginModalOpen(true); }}
                  sx={{ color: '#000000', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.1)' } }}
                >
                  <LoginIcon />
                </IconButton>
              )}
            </Box>
          )}

          {/* Полный мобильный навбар (фильтр, даты, поиск, плитки) — при скролле вниз плитки скрываются */}
          {isMobile && !mobileNavCompact && (
            <>
              <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center', mb: 0.25 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                  <IconButton
                    onClick={() => setBranchPickerOpen(true)}
                    sx={{ color: '#000000', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.1)' } }}
                  >
                    <LocationOnIcon />
                  </IconButton>
                  {selectedBranch ? (() => {
                    const branch = branches.find(b => b.branchId === selectedBranch || b._id === selectedBranch);
                    return branch ? (
                      <Typography variant="body2" sx={{ color: '#000000', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                        {branch.city || ''} - {branch.address || ''}
                      </Typography>
                    ) : null;
                  })() : (
                    <Typography variant="body2" sx={{ color: '#000000', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                      Выберите центр выдачи
                    </Typography>
                  )}
                </Box>
                {user ? (
                  <IconButton onClick={handleMobileMenuOpen} sx={{ color: '#000000', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.1)' } }}>
                    {renderUserAvatar('sm')}
                  </IconButton>
                ) : (
                  <IconButton onClick={() => { setIsRegister(false); setLoginModalOpen(true); }} sx={{ color: '#000000', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.1)' } }}>
                    <LoginIcon />
                  </IconButton>
                )}
              </Box>
              <Box sx={{ display: 'flex', width: '100%', justifyContent: 'flex-start', mb: 0.25, pl: 2 }}>
                <Button
                  variant="text"
                  size="small"
                  onClick={handleDateRangeClick}
                  startIcon={<CalendarTodayIcon />}
                  endIcon={dateFrom && dateTo ? <ClearIcon onClick={(e) => { e.stopPropagation(); handleClearDates(); }} /> : null}
                  sx={{ backgroundColor: 'transparent', color: '#000000', textTransform: 'none', justifyContent: 'flex-start', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.05)' } }}
                >
                  {dateFrom && dateTo
                    ? `${new Date(dateFrom).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' })} - ${new Date(dateTo).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' })}`
                    : 'Период аренды'}
                </Button>
              </Box>
              <SearchField
                value={searchQuery}
                onChange={handleSearchChange}
                onSubmit={handleSearchSubmit}
                onNavigate={(path) => navigate(path)}
                branchId={selectedBranch}
                sx={{ display: 'flex', width: '100%', mb: 0.25 }}
              />
              {/* Плитки навигации под поиском (полный мобильный навбар) */}
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  overflowX: 'auto',
                  width: '100%',
                  py: 0.75,
                  '&::-webkit-scrollbar': { height: 3 },
                  '&::-webkit-scrollbar-thumb': { borderRadius: 2, bgcolor: 'rgba(255,255,255,0.3)' },
                }}
              >
                {[
                  { to: '/rental', label: 'Аренда', icon: <StorefrontIcon sx={{ fontSize: 20 }} /> },
                  { to: '/products', label: 'Товары', icon: <ShoppingCartIcon sx={{ fontSize: 20 }} /> },
                  { to: '/tours', label: 'Туры', icon: <FlightTakeoffIcon sx={{ fontSize: 20 }} /> },
                  { to: '/services', label: 'Услуги', icon: <BuildIcon sx={{ fontSize: 20 }} /> },
                  { to: { pathname: '/partners', hash: '#equipment' }, label: 'Сдать своё оборудование', icon: <Inventory2Icon sx={{ fontSize: 20 }} /> },
                  { to: { pathname: '/partners', hash: '#product' }, label: 'Разместить товар', icon: <SellOutlinedIcon sx={{ fontSize: 20 }} /> },
                  { to: { pathname: '/partners', hash: '#service' }, label: 'Разместить услугу', icon: <AssignmentIcon sx={{ fontSize: 20 }} /> },
                ].map(({ to, label, icon }) => (
                  <Card
                    key={typeof to === 'string' ? to : `${to.pathname}${to.hash || ''}`}
                    component={Link}
                    to={to}
                    sx={{
                      flexShrink: 0,
                      width: 96,
                      minHeight: 70,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      textDecoration: 'none',
                      color: '#000000',
                      border: '1px solid rgba(255,255,255,0.5)',
                      borderRadius: 1.5,
                      backgroundColor: 'rgba(255,255,255,0.9)',
                      '&:hover': { backgroundColor: 'rgba(255,255,255,1)', borderColor: 'rgba(0,0,0,0.2)' },
                    }}
                  >
                    <CardContent sx={{ textAlign: 'center', py: 1, px: 0.75, '&:last-child': { pb: 1 } }}>
                      <Box sx={{ color: '#8B7D6B', mb: 0.25 }}>{icon}</Box>
                      <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.65rem', lineHeight: 1.15, display: 'block' }}>
                        {label}
                      </Typography>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            </>
          )}

          {/* Логотип (скрыт на мобильных) */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 2, flexGrow: 1 }}>
            <Typography
              variant="h6"
              component="div"
              sx={{
                textDecoration: 'none',
                color: '#000000', // Черный цвет из логотипа
                fontWeight: 'bold',
                fontFamily: 'serif',
                cursor: 'pointer',
                letterSpacing: '0.05em',
                minWidth: '120px',
              }}
              onClick={() => (window.location.href = '/')}
            >
              BERLOGA
            </Typography>
            
            <SearchField
              value={searchQuery}
              onChange={handleSearchChange}
              onSubmit={handleSearchSubmit}
              onNavigate={(path) => navigate(path)}
              branchId={selectedBranch}
              sx={{ flexGrow: 1, maxWidth: '600px', mx: 2 }}
            />

            {/* Фильтры для десктопа */}
            {!isMobile && (
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  alignItems: 'center',
                  ml: 'auto',
                }}
              >
                {/* Иконка для выбора центра выдачи (только для десктопа) */}
                <Tooltip title={selectedBranch ? (() => {
                  const branch = branches.find(b => b.branchId === selectedBranch || b._id === selectedBranch);
                  return branch ? `${branch.city || ''} - ${branch.address || ''}` : 'Выбрать центр выдачи';
                })() : 'Выбрать центр выдачи'}>
                  <IconButton
                    onClick={() => setBranchPickerOpen(true)}
                    sx={{
                      color: '#000000',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.1)',
                      },
                    }}
                  >
                    <LocationOnIcon />
                  </IconButton>
                </Tooltip>
                
                {/* Показываем выбранный центр выдачи, если выбран */}
                {selectedBranch ? (() => {
                  const branch = branches.find(b => b.branchId === selectedBranch || b._id === selectedBranch);
                  return branch ? (
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#000000',
                        mr: 1,
                        maxWidth: '200px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {branch.city || ''} - {branch.address || ''}
                    </Typography>
                  ) : null;
                })() : (
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#000000',
                      mr: 1,
                      maxWidth: '200px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Выберите центр выдачи
                  </Typography>
                )}
                
                <Tooltip title="Выбрать период аренды">
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleDateRangeClick}
                    startIcon={<CalendarTodayIcon />}
                    endIcon={dateFrom && dateTo ? <ClearIcon onClick={(e) => { e.stopPropagation(); handleClearDates(); }} /> : null}
                    sx={{
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      borderRadius: '4px',
                      minWidth: '200px',
                      color: '#000000',
                      borderColor: 'rgba(0, 0, 0, 0.23)',
                      mr: 1,
                      '&:hover': {
                        borderColor: 'rgba(0, 0, 0, 0.5)',
                        backgroundColor: 'rgba(255, 255, 255, 1)',
                      },
                    }}
                  >
                    {dateFrom && dateTo 
                      ? `${new Date(dateFrom).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' })} - ${new Date(dateTo).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' })}`
                      : 'Период аренды'
                    }
                  </Button>
                </Tooltip>
              </Box>
            )}
          </Box>

          {/* Десктопная версия: пользователь и корзина (по клику на иконку — боковое меню, как на мобильной) */}
          {!isMobile && (

            <>
              {user ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                  <Tooltip title="Корзина">
                    <IconButton
                      onClick={() => navigate('/cart')}
                      sx={{
                        color: '#000000',
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.1)',
                        },
                      }}
                    >
                      <Badge badgeContent={cartItemsCount} color="error">
                        <ShoppingCartIcon />
                      </Badge>
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Меню">
                    <IconButton
                      onClick={handleMobileMenuOpen}
                      sx={{
                        p: 0,
                        flexShrink: 0,
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.1)',
                        },
                      }}
                    >
                      {renderUserAvatar('md')}
                    </IconButton>
                  </Tooltip>
                </Box>
              ) : (
                <Tooltip title="Вход/Регистрация">
                  <IconButton
                    onClick={() => {
                      setIsRegister(false); // Устанавливаем форму входа
                      setLoginModalOpen(true);
                    }}
                    sx={{ 
                      color: '#000000',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.1)',
                      },
                    }}
                  >
                    <LoginIcon />
                  </IconButton>
                </Tooltip>
              )}
            </>
          )}
        </Toolbar>
      </AppBar>

      {/* Диалог выбора центра выдачи */}
      <Dialog 
        open={branchPickerOpen} 
        onClose={() => setBranchPickerOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Выберите центр выдачи</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <MenuItem 
              onClick={() => {
                handleBranchChange({ target: { value: '' } });
                setBranchPickerOpen(false);
              }}
              sx={{
                backgroundColor: !selectedBranch ? 'rgba(139, 125, 107, 0.2)' : 'transparent',
                '&:hover': {
                  backgroundColor: 'rgba(139, 125, 107, 0.1)',
                },
              }}
            >
              <em>Все центры</em>
            </MenuItem>
            {branches.map((branch) => (
              <MenuItem 
                key={branch._id}
                onClick={() => {
                  handleBranchChange({ target: { value: branch.branchId } });
                  setBranchPickerOpen(false);
                }}
                sx={{
                  backgroundColor: selectedBranch === branch.branchId ? 'rgba(139, 125, 107, 0.2)' : 'transparent',
                  '&:hover': {
                    backgroundColor: 'rgba(139, 125, 107, 0.1)',
                  },
                }}
              >
                {branch.city} - {branch.address}
              </MenuItem>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBranchPickerOpen(false)}>
            Закрыть
          </Button>
        </DialogActions>
      </Dialog>

      {/* Диалог выбора диапазона дат */}
      <Dialog 
        open={datePickerOpen} 
        onClose={handleDateRangeClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Выберите период аренды</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Дата начала"
                value={dateFrom}
                onChange={handleDateFromChange}
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: dateTo || undefined }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: 'rgba(0, 0, 0, 0.23)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(0, 0, 0, 0.5)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#000000',
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Дата окончания"
                value={dateTo}
                onChange={handleDateToChange}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: dateFrom || undefined }}
                disabled={!dateFrom}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: 'rgba(0, 0, 0, 0.23)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(0, 0, 0, 0.5)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#000000',
                    },
                  },
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClearDates} color="error">
            Очистить
          </Button>
          <Button onClick={handleDateRangeClose}>
            Отмена
          </Button>
          <Button 
            onClick={handleDateRangeConfirm} 
            variant="contained" 
            disabled={!dateFrom || !dateTo}
            sx={{ backgroundColor: '#8B7D6B', '&:hover': { backgroundColor: '#6B5D4B' } }}
          >
            Готово
          </Button>
        </DialogActions>
      </Dialog>

      {/* Модальное окно входа и регистрации */}
      <Modal open={loginModalOpen} onClose={() => setLoginModalOpen(false)}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: '#F5F3F0',
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            outline: 'none',
          }}
        >
          {isRegister ? (
            <RegisterPage
              onRegisterSuccess={() => {
                setLoginModalOpen(false);
                window.location.reload(); // Обновляем страницу после успешной регистрации
              }}
              onLoginClick={() => setIsRegister(false)} // Переключаем на форму входа
            />
          ) : (
            <LoginPage
              onLoginSuccess={() => {
                setLoginModalOpen(false);
                window.location.reload(); // Обновляем страницу после входа
              }}
              onRegisterClick={() => setIsRegister(true)} // Переключаем на форму регистрации
            />
          )}
        </Box>
      </Modal>

      {/* Боковое меню для мобильной версии */}
      {renderMobileMenu()}

      {/* Нижнее меню для мобильной версии (скрыто на страницах входа, регистрации, профиля и т.д.) */}
      {isMobile && !['/login', '/register', '/profile', '/profile/settings', '/verify-email', '/reset-password'].some(path => location.pathname.startsWith(path)) && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: '#B8A99A',
            borderTop: '1px solid rgba(0, 0, 0, 0.1)',
            zIndex: 1100,
            boxShadow: '0px -2px 8px rgba(0, 0, 0, 0.1)',
          }}
        >
          <BottomNavigation
            value={
              location.pathname === '/'
                ? '/'
                : location.pathname === '/favorites'
                  ? '/favorites'
                  : location.pathname === '/cart'
                    ? '/cart'
                    : location.pathname === '/orders'
                      ? '/orders'
                      : ''
            }
            onChange={(event, newValue) => {
              if (newValue) {
                navigate(newValue);
              }
            }}
            showLabels
            sx={{
              backgroundColor: '#B8A99A',
              minHeight: 48,
              height: 48,
              '& .MuiBottomNavigationAction-root': {
                color: 'rgba(0, 0, 0, 0.6)',
                minWidth: 56,
                padding: '6px 0',
                '&.Mui-selected': {
                  color: '#000000',
                },
              },
              '& .MuiSvgIcon-root': {
                fontSize: '1.25rem',
              },
              '& .MuiBottomNavigationAction-label': {
                fontSize: '0.7rem',
              },
            }}
          >
            <BottomNavigationAction
              label="Главная"
              value="/"
              icon={<HomeIcon />}
            />
            <BottomNavigationAction
              label="Избранное"
              value="/favorites"
              icon={<FavoriteIcon />}
            />
            <BottomNavigationAction
              label="Корзина"
              value="/cart"
              icon={
                <Badge badgeContent={cartItemsCount} color="error">
                  <ShoppingCartIcon />
                </Badge>
              }
            />
            <BottomNavigationAction
              label="Заказы"
              value="/orders"
              icon={<ReceiptIcon />}
            />
          </BottomNavigation>
        </Box>
      )}
    </>
  );
};

export default AppBarComponent;
